import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const SetupTaskInterface = ({ task, onTaskAction, onComplete, readOnly = false }) => {
  const { t } = useTranslation('operator');
  const [setupData, setSetupData] = useState({
    machineParameters: '',
    calibrationStatus: '',
    setupChecklistComplete: false,
    temperatureSettings: '',
    pressureSettings: '',
    notes: ''
  });
  const [setupComplete, setSetupComplete] = useState(false);

  // Setup checklist items (could be loaded from task config).
  // The English text is the item id stored in checklistStatus; the label is translated at render.
  const checklistItems = [
    'Machine power on and warmed up',
    'Safety guards in place',
    'Material feed aligned',
    'Print heads calibrated',
    'Test print successful',
    'Operating parameters verified'
  ];
  const checklistLabelKeys = {
    'Machine power on and warmed up': 'setupTaskInterface.checklist.machinePowerOn',
    'Safety guards in place': 'setupTaskInterface.checklist.safetyGuards',
    'Material feed aligned': 'setupTaskInterface.checklist.materialFeedAligned',
    'Print heads calibrated': 'setupTaskInterface.checklist.printHeadsCalibrated',
    'Test print successful': 'setupTaskInterface.checklist.testPrintSuccessful',
    'Operating parameters verified': 'setupTaskInterface.checklist.operatingParametersVerified'
  };

  const [checklistStatus, setChecklistStatus] = useState(
    checklistItems.reduce((acc, item) => ({ ...acc, [item]: false }), {})
  );

  useEffect(() => {
    // Check if all checklist items are complete
    const allComplete = checklistItems.every(item => checklistStatus[item]);
    setSetupComplete(allComplete);
  }, [checklistStatus]);

  const handleChecklistChange = (item, checked) => {
    setChecklistStatus(prev => ({
      ...prev,
      [item]: checked
    }));
  };

  const handleInputChange = (field, value) => {
    setSetupData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCompleteSetup = () => {
    if (setupComplete) {
      onComplete({
        taskId: task.task_id,
        setupData,
        checklistStatus,
        completedAt: new Date().toISOString()
      });
    }
  };

  return (
    <div className="setup-task-interface">
      <div className="task-header">
        <h3>🔧 {task.task_name}</h3>
        <div className="task-type-badge setup">
          {t('taskTypes.setup')}
        </div>
      </div>

      <div className="task-description">
        <p>{task.task_description}</p>
        {task.instructions && (
          <div className="instructions">
            <h4>{t('labels.instructions')}</h4>
            <p>{task.instructions}</p>
          </div>
        )}
      </div>

      {/* Machine Parameters Section */}
      <div className="setup-section">
        <h4>{t('setupTaskInterface.machineParameters')}</h4>
        <div className="parameter-grid">
          <div className="parameter-field">
            <label>{t('setupTaskInterface.temperatureSettings')}</label>
            <input
              type="text"
              value={setupData.temperatureSettings}
              onChange={(e) => handleInputChange('temperatureSettings', e.target.value)}
              placeholder={t('setupTaskInterface.temperaturePlaceholder')}
              className="parameter-input"
              disabled={readOnly}
            />
          </div>
          <div className="parameter-field">
            <label>{t('setupTaskInterface.pressureSettings')}</label>
            <input
              type="text"
              value={setupData.pressureSettings}
              onChange={(e) => handleInputChange('pressureSettings', e.target.value)}
              placeholder={t('setupTaskInterface.pressurePlaceholder')}
              className="parameter-input"
              disabled={readOnly}
            />
          </div>
          <div className="parameter-field full-width">
            <label>{t('setupTaskInterface.machineParameters')}</label>
            <textarea
              value={setupData.machineParameters}
              onChange={(e) => handleInputChange('machineParameters', e.target.value)}
              placeholder={t('setupTaskInterface.machineParametersPlaceholder')}
              className="parameter-textarea"
              rows="3"
              disabled={readOnly}
            />
          </div>
        </div>
      </div>

      {/* Setup Checklist Section */}
      <div className="setup-section">
        <h4>{t('setupTaskInterface.setupChecklist')}</h4>
        <div className="checklist">
          {checklistItems.map((item, index) => (
            <div key={index} className="checklist-item">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={checklistStatus[item] || false}
                  onChange={(e) => handleChecklistChange(item, e.target.checked)}
                  className="checklist-checkbox"
                  disabled={readOnly}
                />
                <span className="checkmark"></span>
                {t(checklistLabelKeys[item])}
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* Calibration Status */}
      <div className="setup-section">
        <h4>{t('setupTaskInterface.calibrationStatus')}</h4>
        <select
          value={setupData.calibrationStatus}
          onChange={(e) => handleInputChange('calibrationStatus', e.target.value)}
          className="calibration-select"
          disabled={readOnly}
        >
          <option value="" disabled hidden>{t('setupTaskInterface.selectCalibrationStatus')}</option>
          <option value="not-required">{t('setupTaskInterface.calibration.notRequired')}</option>
          <option value="in-progress">{t('setupTaskInterface.calibration.inProgress')}</option>
          <option value="completed">{t('setupTaskInterface.calibration.completed')}</option>
          <option value="failed">{t('setupTaskInterface.calibration.failed')}</option>
        </select>
      </div>

      {/* Safety Notes */}
      {task.safety_notes && (
        <div className="setup-section safety-notes">
          <h4>⚠️ {t('setupTaskInterface.safetyNotes')}</h4>
          <div className="safety-content">
            {task.safety_notes}
          </div>
        </div>
      )}

      {/* Notes Section */}
      <div className="setup-section">
        <h4>{t('labels.notes')}</h4>
        <textarea
          value={setupData.notes}
          onChange={(e) => handleInputChange('notes', e.target.value)}
          placeholder={t('placeholders.notes')}
          className="notes-textarea"
          rows="3"
          disabled={readOnly}
        />
      </div>

      {/* Action Buttons */}
      {!readOnly && <div className="task-actions">
        <button
          className={`btn btn-primary ${!setupComplete ? 'disabled' : ''}`}
          onClick={handleCompleteSetup}
          disabled={!setupComplete}
        >
          {setupComplete ? t('buttons.complete') : t('setupTaskInterface.completeChecklist')}
        </button>

        <div className="setup-progress">
          <div className="progress-text">
            {t('setupTaskInterface.setupProgress', { done: Object.values(checklistStatus).filter(Boolean).length, total: checklistItems.length })}
          </div>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{
                width: `${(Object.values(checklistStatus).filter(Boolean).length / checklistItems.length) * 100}%`
              }}
            />
          </div>
        </div>
      </div>}

      <style jsx>{`
        .setup-task-interface {
          padding: 20px;
          background: #f8f9fa;
          border-radius: 8px;
          margin-bottom: 20px;
        }

        .task-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          padding-bottom: 15px;
          border-bottom: 2px solid #007bff;
        }

        .task-header h3 {
          margin: 0;
          color: #2c3e50;
          font-size: 1.3em;
        }

        .task-type-badge {
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 0.85em;
          font-weight: bold;
          text-transform: uppercase;
        }

        .task-type-badge.setup {
          background: #007bff;
          color: white;
        }

        .setup-section {
          margin-bottom: 25px;
          background: white;
          padding: 18px;
          border-radius: 6px;
          border-left: 4px solid #007bff;
        }

        .setup-section h4 {
          margin: 0 0 15px 0;
          color: #2c3e50;
          font-size: 1.1em;
        }

        .parameter-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 15px;
        }

        .parameter-field.full-width {
          grid-column: 1 / -1;
        }

        .parameter-field label {
          display: block;
          margin-bottom: 6px;
          font-weight: 500;
          color: #495057;
        }

        .parameter-input, .parameter-textarea, .calibration-select, .notes-textarea {
          width: 100%;
          padding: 10px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 14px;
          transition: border-color 0.2s;
        }

        .parameter-input:focus, .parameter-textarea:focus, .calibration-select:focus, .notes-textarea:focus {
          outline: none;
          border-color: #007bff;
          box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.1);
        }

        .checklist {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 12px;
        }

        .checklist-item {
          background: #f8f9fa;
          padding: 12px;
          border-radius: 4px;
          border: 1px solid #e9ecef;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          cursor: pointer;
          font-size: 14px;
          user-select: none;
        }

        .checklist-checkbox {
          margin-right: 10px;
          transform: scale(1.2);
        }

        .safety-notes {
          border-left-color: #ffc107;
          background: #fff8e1;
        }

        .safety-content {
          background: white;
          padding: 12px;
          border-radius: 4px;
          border: 1px solid #ffc107;
        }

        .task-actions {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 30px;
          padding-top: 20px;
          border-top: 1px solid #dee2e6;
        }

        .btn {
          padding: 12px 24px;
          border: none;
          border-radius: 6px;
          font-size: 16px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-primary {
          background: #007bff;
          color: white;
        }

        .btn-primary:hover:not(.disabled) {
          background: #0056b3;
          transform: translateY(-1px);
        }

        .btn.disabled {
          background: #6c757d;
          cursor: not-allowed;
          opacity: 0.6;
        }

        .setup-progress {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
        }

        .progress-text {
          font-size: 14px;
          color: #6c757d;
          margin-bottom: 8px;
        }

        .progress-bar {
          width: 200px;
          height: 8px;
          background: #e9ecef;
          border-radius: 4px;
          overflow: hidden;
        }

        .progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #007bff, #0056b3);
          transition: width 0.3s ease;
        }

        .instructions {
          background: #e3f2fd;
          padding: 15px;
          border-radius: 6px;
          margin-top: 10px;
          border-left: 4px solid #2196f3;
        }

        .instructions h4 {
          margin-top: 0;
          color: #1976d2;
        }
      `}</style>
    </div>
  );
};

export default SetupTaskInterface;